// Model calls: one-shot completion + SSE streaming, both traced.

import { API, apiFetch } from "./client";
import { PRI, type Priority } from "./scheduler";
import { trace } from "../core/telemetry";

export interface CompleteOpts {
  max?: number;
  temp?: number;
  signal?: AbortSignal;
  priority?: Priority;
  /** Telemetry label — what this call is for (e.g. "nav.propose"). */
  kind?: string;
}

export async function complete(prompt: string, { max = 340, temp = 0.6, signal, priority, kind = "complete" }: CompleteOpts = {}): Promise<string> {
  const t0 = performance.now();
  const ev = { ts: Date.now(), kind, priority, promptChars: prompt.length, ok: false } as Parameters<typeof trace>[0];
  try {
    const { res, httpRetries } = await apiFetch({
      model: API.model, max_tokens: max, temperature: temp,
      messages: [{ role: "user", content: prompt }],
    }, { signal, priority });
    const j = await res.json();
    const out: string = j.choices?.[0]?.message?.content ?? "";
    Object.assign(ev, { ok: true, httpRetries, outputChars: out.length, latencyMs: Math.round(performance.now() - t0) });
    return out;
  } catch (e) {
    Object.assign(ev, { error: String((e as Error).message || e), latencyMs: Math.round(performance.now() - t0) });
    throw e;
  } finally {
    trace(ev);
  }
}

export async function* streamComplete(prompt: string, { max = 1400, temp = 0.7, signal, kind = "stream" }: CompleteOpts = {}): AsyncGenerator<string> {
  const t0 = performance.now();
  const ev = { ts: Date.now(), kind, stream: true, priority: PRI.FG, promptChars: prompt.length, ok: false } as Parameters<typeof trace>[0];
  let outputChars = 0;
  try {
    const { res, httpRetries } = await apiFetch({
      model: API.model, max_tokens: max, temperature: temp, stream: true,
      messages: [{ role: "user", content: prompt }],
    }, { signal, priority: PRI.FG });
    ev.httpRetries = httpRetries;
    if (!res.body) throw new Error("no stream body");
    const reader = res.body.getReader();
    const dec = new TextDecoder();
    let buf = "";
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += dec.decode(value, { stream: true });
      const lines = buf.split("\n");
      buf = lines.pop()!;
      for (const line of lines) {
        const t = line.trim();
        if (!t.startsWith("data:")) continue;
        const payload = t.slice(5).trim();
        if (payload === "[DONE]") { ev.ok = true; return; }
        try {
          const piece = JSON.parse(payload).choices?.[0]?.delta?.content;
          if (piece) {
            if (outputChars === 0) ev.ttftMs = Math.round(performance.now() - t0);
            outputChars += piece.length;
            yield piece;
          }
        } catch { /* ignore keepalive */ }
      }
    }
    ev.ok = true;
  } catch (e) {
    ev.error = String((e as Error).message || e);
    throw e;
  } finally {
    ev.outputChars = outputChars;
    ev.latencyMs = Math.round(performance.now() - t0);
    trace(ev);
  }
}
